import React, { useEffect, useState, useMemo, useRef } from 'react';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import Router, { useRouter } from 'next/router';
import toast, { ToastBar, Toaster } from 'react-hot-toast';
import { SSRKeycloakProvider, SSRCookies } from '@react-keycloak/ssr';
import nProgress from 'nprogress';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

// Internal imports
import { ApiConsoleComponent } from '../components/astur/api-console';
import { useAppData } from '../hooks/useAppData';
import { AppDataConsumer, AppDataProvider } from '../contexts/AppdataContext';
import { OrganizationGatedProviders } from '../componentsv2/app/OrganizationGatedProviders';
import { createTheme } from '../theme';
import { KeycloakSessionBridge } from '@/session/KeycloakSessionBridge';
import { isSessionTransition } from '@/session/operation';
import { OrganizationScope, useRouteOrganization } from '@/session/OrganizationIdentityContext';

// Styles
import '../i18n';
import { utils } from '../utils';
import { pushIntercomEvent, getDashboardUrl } from '../utils/intercomDataLayer';
import { sanitizeTelemetryPath } from '@/session/telemetry';

// Router events
Router.events.on('routeChangeStart', nProgress.start);
Router.events.on('routeChangeError', (err: Error & { cancelled?: boolean }) => {
  // Only log if it's not a cancelled navigation
  if (!err.cancelled) {
    console.error('Route change error:', err);
  }
  nProgress.done();
});
Router.events.on('routeChangeComplete', nProgress.done);

// Client-side emotion cache
const clientSideEmotionCache = utils.emotionCache.createEmotionCache();

// Extended AppProps to include emotionCache and cookies
interface CustomAppProps extends AppProps {
  emotionCache?: EmotionCache;
  cookies?: any;
}

// Component with getLayout property
type ComponentWithLayout = React.ComponentType<any> & {
  getLayout?: (page: React.ReactElement) => React.ReactElement;
  skipOrganizationGate?: boolean;
};

// Keycloak configuration type
interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
  cors: boolean;
}

// Configuration type with optional matchHost
type ConfigEntry = {
  keycloakUrl: string;
  apiHost: string;
  integrationsHost: string;
  channelApiHost: string;
  matchHost?: string;
};

let bugsnagStarted = false;
let globalHandlersInstalled = false;
let originalConsoleError: typeof console.error | null = null;
let previousWindowOnError: OnErrorEventHandler | null = null;
let unhandledRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

const isBugsnagInternalError = (args: unknown[]): boolean => {
  return args.some((arg) => typeof arg === 'string' && arg.includes('[bugsnag]'));
};

const isBugsnagSessionDeliveryNoise = (args: unknown[]): boolean => {
  const message = args.map(utils.helpers.stringifyConsoleArg).join(' ');
  return message.includes('[bugsnag] Session failed to send') && message.includes('status 0');
};

// Global error handlers setup
const setupGlobalErrorHandlers = (): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  if (globalHandlersInstalled) return () => {};
  globalHandlersInstalled = true;

  // Capture unhandled JavaScript errors
  previousWindowOnError = window.onerror;
  window.onerror = (_message, _source, lineno, colno, error) => {
    Bugsnag.notify(new Error('Unhandled window error'), (event) => {
      event.addMetadata('window_error', {
        lineno,
        colno,
        path: sanitizeTelemetryPath(window.location.href),
        errorCode: error?.name,
      });
    });
    return false;
  };

  // Capture unhandled promise rejections
  unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
    // Lease cancellations are control flow (organization switch teardown),
    // not errors; this also covers any future unguarded call site.
    if (isSessionTransition(event.reason)) return;
    Bugsnag.notify(new Error('Unhandled promise rejection'), (bugsnagEvent) => {
      bugsnagEvent.addMetadata('promise_rejection_error', {
        path: sanitizeTelemetryPath(window.location.href),
        errorCode: event.reason instanceof Error ? event.reason.name : undefined,
      });
    });
  };
  window.addEventListener('unhandledrejection', unhandledRejectionHandler);

  // Override console.error to also send to Bugsnag
  originalConsoleError = originalConsoleError || console.error;
  console.error = (...args: unknown[]) => {
    // In local dev, ad blockers commonly block Bugsnag sessions and spam this exact error.
    if (process.env.NODE_ENV !== 'production' && isBugsnagSessionDeliveryNoise(args)) {
      return;
    }

    // Call original console.error first
    originalConsoleError?.apply(console, args);
    if (isBugsnagInternalError(args)) {
      return;
    }
    if (args.some(isSessionTransition)) return;

    Bugsnag.notify(new Error('Console error'), (event) => {
      event.addMetadata('console_error', {
        path: sanitizeTelemetryPath(window.location.href),
        errorCode: args[0] instanceof Error ? args[0].name : undefined,
      });
    });
  };

  return () => {
    if (typeof window === 'undefined') return;
    if (unhandledRejectionHandler) {
      window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
      unhandledRejectionHandler = null;
    }
    if (originalConsoleError) {
      console.error = originalConsoleError;
    }
    window.onerror = previousWindowOnError;
    previousWindowOnError = null;
    globalHandlersInstalled = false;
  };
};

const App = ({ Component, emotionCache = clientSideEmotionCache, pageProps, cookies }: CustomAppProps) => {
  const ComponentWithLayoutTyped = Component as ComponentWithLayout;
  const getLayout = ComponentWithLayoutTyped.getLayout ?? ((page: React.ReactElement) => page);
  const router = useRouter();
  const routeOrganization = useRouteOrganization();
  const { appdata } = useAppData();
  const [gotConfig, setGotConfig] = useState<boolean>(false);
  const appDataRef = useRef(appdata);
  const intercomBootedRef = useRef(false);

  useEffect(() => {
    appDataRef.current = appdata;
  }, [appdata]);

  // Memoize keycloak config to prevent re-initialization
  const keycloakConfig = useMemo((): KeycloakConfig => {
    let keycloakUrl = utils.constants.ASTUR_DEFAULTS.config[utils.constants.ASTUR_DEFAULTS.defaultConfig].keycloakUrl;

    // Only do client-side host matching if we're in the browser
    if (typeof window !== 'undefined') {
      for (const configKey in utils.constants.ASTUR_DEFAULTS.config) {
        const config = utils.constants.ASTUR_DEFAULTS.config[configKey as keyof typeof utils.constants.ASTUR_DEFAULTS.config] as ConfigEntry;
        if (typeof config.matchHost === 'undefined') continue;
        if (window.location.host.includes(config.matchHost)) {
          keycloakUrl = config.keycloakUrl;
          break;
        }
      }
    }

    return {
      url:
        typeof window !== 'undefined' && typeof (window as any).ASTUR_FRONTEND_KEYCLOAK_URL !== 'undefined'
          ? (window as any).ASTUR_FRONTEND_KEYCLOAK_URL
          : keycloakUrl,
      realm:
        typeof window !== 'undefined' && typeof (window as any).ASTUR_FRONTEND_KEYCLOAK_REALM !== 'undefined'
          ? (window as any).ASTUR_FRONTEND_KEYCLOAK_REALM
          : utils.constants.ASTUR_DEFAULTS.keycloakRealm,
      clientId:
        typeof window !== 'undefined' && typeof (window as any).ASTUR_FRONTEND_KEYCLOAK_CLIENT_ID !== 'undefined'
          ? (window as any).ASTUR_FRONTEND_KEYCLOAK_CLIENT_ID
          : utils.constants.ASTUR_DEFAULTS.keycloakClientId,
      cors: false,
    };
  }, []); // Empty dependency array means this never changes

  useEffect(() => {
    if (!bugsnagStarted) {
      bugsnagStarted = true;
      const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

      Bugsnag.start({
        apiKey: process.env.BUGSNAG_API_KEY || '4447a8f6bdd11c0e8d37852d435080ba', // TODO(nicksherron): Remove the hardcoded key
        autoDetectErrors: true,
        autoTrackSessions: !isLocalhost,
        appVersion:
          process.env.NEXT_DEPLOYMENT_ID || // Legacy fallback; deployments report the Git hash through NEXT_PUBLIC_BUILD_ID.
          process.env.NEXT_PUBLIC_BUILD_ID ||
          process.env.npm_package_version ||
          require('../../package.json').version,
        plugins: [new BugsnagPluginReact()],
        enabledReleaseStages: ['production', 'development'],
        releaseStage: process.env.NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE || process.env.NODE_ENV || 'development',
        onError: function (event) {
          if (event.errors && event.errors[0] && event.errors[0].errorMessage === 'ResizeObserver loop completed with undelivered notifications.') {
            return false;
          }

          // Add app context to all errors
          if (typeof window !== 'undefined') {
            event.addMetadata('appContext', {
              theme: appDataRef.current?.theme,
              path: sanitizeTelemetryPath(window.location.href),
            });
          }

          return true;
        },
      });
    }

    // Set up global error handlers
    const cleanup = setupGlobalErrorHandlers();

    return cleanup;
  }, []);

  useEffect(() => {
    if (!router.isReady || intercomBootedRef.current) return;
    intercomBootedRef.current = true;
    const sitename = routeOrganization ?? '';
    pushIntercomEvent('intercom_boot', {
      sitename,
      dashboard_url: getDashboardUrl(sitename),
    });
  }, [routeOrganization, router.isReady]);

  useEffect(() => {
    queueMicrotask(() => setGotConfig(true));
  }, []);

  // Create themes. App re-renders on every route change, so an unmemoized
  // createTheme would hand ThemeProvider a new context value each navigation
  // and re-render every styled()/sx consumer in the tree.
  const darkTheme = useMemo(
    () =>
      createTheme({
        direction: appdata.direction,
        responsiveFontSizes: appdata.responsiveFontSizes,
        mode: 'dark',
      }),
    [appdata.direction, appdata.responsiveFontSizes]
  );
  const lightTheme = useMemo(
    () =>
      createTheme({
        direction: appdata.direction,
        responsiveFontSizes: appdata.responsiveFontSizes,
        mode: 'light',
      }),
    [appdata.direction, appdata.responsiveFontSizes]
  );

  const handleKeycloakEvent = (event: string, error?: any): void => {
    if (event === 'onInitError') {
      // redirect to keycloak logout endpoint
      if (typeof window !== 'undefined') {
        window.location.assign(
          keycloakConfig.url +
            '/realms/' +
            keycloakConfig.realm +
            '/protocol/openid-connect/logout?post_logout_redirect_uri=' +
            window.location.origin +
            '&client_id=' +
            keycloakConfig.clientId
        );
      }
    }
  };

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>Acenda</title>
        <meta name='viewport' content='initial-scale=1, width=device-width' />
      </Head>
      <Script id='gtm' strategy='afterInteractive'>
        {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-TWPC6LTZ');
        `}
      </Script>

      {gotConfig && (
        <AppDataProvider>
          <noscript
            dangerouslySetInnerHTML={{
              __html: `
              <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TWPC6LTZ"
              height="0" width="0" style="display:none;visibility:hidden"></iframe>
              `,
            }}
          />
          <AppDataConsumer>
            {({ appdata }) => (
              <ThemeProvider theme={appdata.theme === 'dark' ? darkTheme : lightTheme}>
                <CssBaseline />
                <Toaster
                  position='top-right'
                  containerStyle={{
                    top: 80,
                    left: 20,
                    bottom: 20,
                    right: 20,
                  }}
                  toastOptions={{
                    className: '',
                    duration: 5000,
                    style: {
                      zIndex: 9999,
                    },
                  }}
                >
                  {(t) => (
                    <ToastBar toast={t}>
                      {({ icon, message }) => (
                        <>
                          {icon}
                          {message}
                          {t.type !== 'loading' && <button onClick={() => toast.dismiss(t.id)}>X</button>}
                        </>
                      )}
                    </ToastBar>
                  )}
                </Toaster>
                {React.createElement(
                  SSRKeycloakProvider as any,
                  {
                    keycloakConfig,
                    // check-sso runs in a hidden iframe instead of a full-page
                    // redirect that boots the app twice. The callback page is
                    // load-bearing: keycloak-js never times out the silent
                    // check, so a missing public/silent-check-sso.html hangs
                    // sign-in. Browsers that block Keycloak's cookies in the
                    // iframe fall back to the full-page redirect.
                    initOptions: {
                      onLoad: 'check-sso',
                      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
                    },
                    onEvent: handleKeycloakEvent,
                    persistor: SSRCookies(cookies),
                  },
                  <>
                    <ApiConsoleComponent />
                    <KeycloakSessionBridge>
                      <OrganizationScope>
                        <OrganizationGatedProviders page={ComponentWithLayoutTyped}>
                          {getLayout(<Component {...pageProps} />)}
                        </OrganizationGatedProviders>
                      </OrganizationScope>
                    </KeycloakSessionBridge>
                  </>
                )}
              </ThemeProvider>
            )}
          </AppDataConsumer>
        </AppDataProvider>
      )}
    </CacheProvider>
  );
};

export default App;
